<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { useExactTimestamp } from 'shared/composables/useExactTimestamp';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useManager } from 'dashboard/composables/useManager';
import ContactInfoRow from './ContactInfoRow.vue';
import ExtraAdditionalAttributes from './ExtraAdditionalAttributes.vue';
import ViewAllConversations from './ViewAllConversations.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SocialIcons from './SocialIcons.vue';
import CreateJiraIssueModal from '../../../../components/widgets/conversation/Jira/CreateIssueModal.vue';
import ListJiraIssuesModal from '../../../../components/widgets/conversation/Jira/ListIssuesModal.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ContactDeleteModal from 'dashboard/modules/contact/ContactDeleteModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';

import { copyTextToClipboard } from 'shared/helpers/clipboard';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    ViewAllConversations,
    EditContact,
    Avatar,
    ComposeConversation,
    SocialIcons,
    ContactMergeModal,
    ExtraAdditionalAttributes,
    CreateJiraIssueModal,
    ListJiraIssuesModal,
    ContactDeleteModal,
    VoiceCallButton,
    InlineInput,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    hideDeleteContactButton: {
      type: Boolean,
      default: false,
    },
    hideElementsForGroup: {
      type: Boolean,
      default: false,
    },
    conversationProtocol: {
      type: [String, Number],
      default: '',
    },
    showProtocol: {
      type: Boolean,
      default: false,
    },
    showJiraIssueButton: {
      type: Boolean,
      default: false,
    },
    inboxId: {
      type: Number,
      default: null,
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    const { isManager } = useManager();
    return {
      isAdmin,
      isManager,
      exactTimestamp: useExactTimestamp(),
    };
  },
  data() {
    return {
      showEditModal: false,
      showDeleteModal: false,
      showJiraIssueModal: false,
      showListJiraIssuesModal: false,
      fetchingProfilePicture: false,
      isEditingName: false,
      editName: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;

      const telegram = socialProfiles?.telegram || telegramUsername || '';
      const twitter = socialProfiles?.twitter || twitterScreenName || '';

      return {
        ...(socialProfiles || {}),
        twitter,
        telegram,
      };
    },
    extraAdds() {
      const { extraAdds } = this.additionalAttributes;
      return extraAdds || {};
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
    ableToFetchNewAvatar() {
      return this.channelType === 'Channel::CommonWhatsapp';
    },
    whatsappUsername() {
      const username =
        this.socialProfiles.whatsapp ||
        this.additionalAttributes.social_whatsapp_user_name ||
        '';

      return username.toString().replace(/^@+/, '');
    },
    formattedWhatsappUsername() {
      return this.whatsappUsername ? `@${this.whatsappUsername}` : '';
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    startEditingName() {
      this.editName = this.contact.name || '';
      this.isEditingName = true;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },
    saveNameEdit() {
      if (!this.isEditingName) return;
      this.isEditingName = false;
      const trimmed = this.editName.trim();
      if (trimmed && trimmed !== this.contact.name) {
        this.updateContactField({ name: trimmed });
      }
    },
    cancelNameEdit() {
      this.isEditingName = false;
    },
    onFieldUpdate(field, value) {
      this.updateContactField({ [field]: value });
    },
    async updateContactField(attrs) {
      const contactId = this.contact.id;
      try {
        await this.$store.dispatch('contacts/update', {
          id: contactId,
          ...attrs,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        await this.$store.dispatch('contacts/fetchContactableInbox', contactId);
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const detail = error.contactErrorDetail;
          if (detail) {
            useAlert(detail);
          } else {
            const invalidAttrs = Array.isArray(error.data) ? error.data : [];
            if (invalidAttrs.includes('email')) {
              useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
            } else if (invalidAttrs.includes('phone_number')) {
              useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
            } else {
              useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
            }
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
    openMergeModal() {
      this.$refs.mergeModal?.open();
    },
    async onCopy(e) {
      e.preventDefault();
      await copyTextToClipboard(this.conversationProtocol);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    async fetchProfilePicture() {
      this.fetchingProfilePicture = true;
      try {
        await this.$store.dispatch('contacts/fetchAvatarFromChannel', {
          id: this.contact.id,
          inboxId: this.inboxId,
        });
        this.fetchingProfilePicture = false;
        useAlert(this.$t('FETCH_PROFILE_PICTURE.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.fetchingProfilePicture = false;
        useAlert(this.$t('FETCH_PROFILE_PICTURE.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">
      <div class="flex flex-row justify-between">
        <Avatar
          v-if="showAvatar"
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="48"
          hide-offline-status
        />
      </div>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-2">
          <div class="group/name flex items-center min-w-0 gap-2">
            <InlineInput
              v-if="isEditingName"
              ref="nameInput"
              v-model="editName"
              custom-input-class="!text-base !font-medium !w-auto max-w-full [field-sizing:content]"
              class="!w-fit min-w-0"
              @enter-press="saveNameEdit"
              @escape-press="cancelNameEdit"
              @blur="saveNameEdit"
            />
            <h3
              v-else
              class="flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12 cursor-pointer hover:text-n-slate-12/80"
              :title="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
              @click="startEditingName"
            >
              {{ contact.name }}
            </h3>
            <NextButton
              ghost
              xs
              slate
              icon="i-lucide-pencil"
              :title="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
              class="flex-shrink-0 -mx-1 opacity-0 transition-opacity"
              :class="
                isEditingName
                  ? 'invisible'
                  : 'group-hover/name:opacity-100 focus-visible:opacity-100'
              "
              @click="startEditingName"
            />
          </div>
          <div class="flex flex-row items-center gap-2">
            <span
              v-if="contact.created_at && !hideElementsForGroup"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${exactTimestamp(
                  contact.created_at
                )}`
              "
              class="i-lucide-info text-sm text-n-slate-10"
            />
            <a
              v-if="!hideElementsForGroup"
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link text-sm text-n-slate-10" />
            </a>
          </div>
        </div>

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            v-if="!hideElementsForGroup"
            :href="contact.email ? `mailto:${contact.email}` : ''"
            :value="contact.email"
            icon="mail"
            emoji="✉️"
            :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
            show-copy
            editable
            @update="value => onFieldUpdate('email', value)"
          />
          <ContactInfoRow
            v-if="!hideElementsForGroup"
            :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
            :value="contact.phone_number"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
            show-copy
            editable
            @update="value => onFieldUpdate('phone_number', value)"
          />
          <ExtraAdditionalAttributes :extra-adds="extraAdds" />
          <ContactInfoRow
            v-if="formattedWhatsappUsername"
            :value="formattedWhatsappUsername"
            icon="brand-whatsapp"
            emoji="💬"
            :title="$t('CONTACT_PANEL.WHATSAPP_USERNAME')"
            show-copy
          />
          <ContactInfoRow
            v-if="contact.identifier && !hideElementsForGroup"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.IDENTIFIER')"
          />
          <ContactInfoRow
            v-if="!hideElementsForGroup"
            :value="additionalAttributes.company_name"
            icon="building-bank"
            emoji="🏢"
            :title="$t('CONTACT_PANEL.COMPANY')"
            editable
            @update="
              value =>
                updateContactField({
                  additional_attributes: {
                    ...additionalAttributes,
                    company_name: value,
                  },
                })
            "
          />
          <ContactInfoRow
            v-if="
              (location || additionalAttributes.location) &&
              !hideElementsForGroup
            "
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
            :title="$t('CONTACT_PANEL.LOCATION')"
          />
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
      </div>
      <div
        v-if="showProtocol"
        v-tooltip="$t('CONVERSATION.PROTOCOL_TITLE')"
        class="text-sm bg-yellow-100 text-slate-900 dark:text-yellow-100 dark:bg-slate-800 flex align-middle mt-2 p-1 pl-2 rounded-sm"
      >
        <b>{{ $t('CONVERSATION.HEADER.PROTOCOL_LABEL') }}:</b>
        {{ conversationProtocol || $t('CONVERSATION.NO_PROTOCOL') }}
        <woot-button
          v-if="conversationProtocol"
          type="submit"
          variant="clear"
          size="tiny"
          color-scheme="secondary"
          icon="clipboard"
          class-names="copy-button"
          @click="onCopy"
        />
      </div>
      <!-- CREATE JIRA ISSUE BUTTON -->
      <div v-if="showJiraIssueButton">
        <div
          class="w-full justify-center text-center align-middle bg-woot-600 hover:bg-woot-700 cursor-pointer text-white p-2 rounded-md text-sm flex gap-2 font-bold"
          @click="() => (showJiraIssueModal = true)"
        >
          {{ $t('INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.BUTTON') }}
          <fluent-icon class="mt-1" icon="add-circle" size="16" />
        </div>
        <!-- SHOW JIRA ISSUES BUTTON -->
        <div
          class="w-full justify-center text-center align-middle bg-yellow-500 hover:bg-yellow-600 cursor-pointer text-white p-2 rounded-md text-sm mt-2 flex gap-2 font-bold"
          @click="() => (showListJiraIssuesModal = true)"
        >
          {{ $t('INTEGRATION_SETTINGS.JIRA.SHOW_TICKET.BUTTON') }}
          <fluent-icon class="mt-1" icon="search" size="16" />
        </div>
      </div>
      <div class="flex items-center w-full mt-0.5 gap-2">
        <ComposeConversation :contact-id="String(contact.id)">
          <template #trigger>
            <NextButton
              v-if="!hideElementsForGroup"
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chat-circle-dots"
              slate
              faded
              sm
            />
          </template>
        </ComposeConversation>
        <ViewAllConversations :contact="contact" />
        <VoiceCallButton
          :phone="contact.phone_number"
          :contact-id="contact.id"
          :conversation-id="currentChat?.id"
          icon="i-lucide-phone"
          sm
          faded
          slate
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
        />
        <NextButton
          v-if="!hideElementsForGroup"
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <NextButton
          v-if="!hideElementsForGroup && ableToFetchNewAvatar"
          v-tooltip="$t('FETCH_PROFILE_PICTURE.BUTTON_LABEL')"
          :title="$t('FETCH_PROFILE_PICTURE.BUTTON_LABEL')"
          icon="repeat"
          variant="smooth"
          color-scheme="warning"
          size="small"
          :is-loading="fetchingProfilePicture"
          @click="fetchProfilePicture"
        />
        <ContactMergeModal :primary-contact="contact">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
              icon="i-ph-arrows-merge"
              slate
              faded
              sm
              :disabled="uiFlags.isMerging"
            />
          </template>
        </ContactMergeModal>
        <ContactDeleteModal
          v-if="isAdmin || isManager"
          :contact="contact"
          @deleted="$emit('panelClose')"
        >
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
              icon="i-ph-trash"
              slate
              faded
              sm
              ruby
              :disabled="uiFlags.isDeleting"
            />
          </template>
        </ContactDeleteModal>
      </div>
      <EditContact
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
    </div>
    <woot-modal
      v-if="showJiraIssueModal"
      v-model:show="showJiraIssueModal"
      :on-close="() => (showJiraIssueModal = false)"
      size="medium"
    >
      <CreateJiraIssueModal @close="() => (showJiraIssueModal = false)" />
    </woot-modal>
    <woot-modal
      v-if="showListJiraIssuesModal"
      v-model:show="showListJiraIssuesModal"
      :on-close="() => (showListJiraIssuesModal = false)"
      size="medium"
    >
      <ListJiraIssuesModal
        :contact="contact"
        @close="() => (showListJiraIssuesModal = false)"
      />
    </woot-modal>
  </div>
</template>
